import React, { useState, useEffect } from 'react';
import { useAccount, useWriteContract, useWaitForTransactionReceipt, useReadContract, useWatchContractEvent } from 'wagmi';
import { useSearchParams } from 'react-router-dom';
import { formatEther } from 'viem';
import { CONTRACT_ADDRESS } from '../config/wagmi';
import RedEnvelopeABI from '../abis/RedEnvelope.json';
import './ClaimEnvelope.css';

interface EnvelopeInfo {
  creator: string;
  totalAmount: bigint;
  remainingAmount: bigint;
  totalCount: bigint;
  remainingCount: bigint;
  createTime: bigint;
  isActive: boolean;
}

const ClaimEnvelope: React.FC = () => {
  const { address, isConnected } = useAccount();
  const [searchParams] = useSearchParams();
  const [envelopeId, setEnvelopeId] = useState<string>(searchParams.get('envelopeId') || '');
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(null);
  const [envelopeInfo, setEnvelopeInfo] = useState<EnvelopeInfo | null>(null);

  // 抢红包
  const { writeContract: claimEnvelope, data: claimHash, isPending: isClaiming } = useWriteContract();
  const { isLoading: isClaimingConfirming } = useWaitForTransactionReceipt({
    hash: claimHash,
  });

  // 读取红包信息
  // @ts-expect-error - Wagmi v3 type definitions issue
  const { data: envelopeData, refetch: refetchEnvelope } = useReadContract({
    address: CONTRACT_ADDRESS ? (CONTRACT_ADDRESS as `0x${string}`) : undefined,
    abi: RedEnvelopeABI,
    functionName: 'getEnvelopeInfo',
    args: envelopeId ? [BigInt(envelopeId)] : undefined,
    query: {
      enabled: !!envelopeId && !!CONTRACT_ADDRESS,
    },
  });

  // 检查是否已抢过
  // @ts-expect-error - Wagmi v3 type definitions issue
  const { data: hasClaimed } = useReadContract({
    address: CONTRACT_ADDRESS ? (CONTRACT_ADDRESS as `0x${string}`) : undefined,
    abi: RedEnvelopeABI,
    functionName: 'hasClaimed',
    args: envelopeId && address ? [BigInt(envelopeId), address] : undefined,
    query: {
      enabled: !!envelopeId && !!address && !!CONTRACT_ADDRESS,
    },
  });

  // 监听事件
  // @ts-expect-error - Wagmi v3 type definitions issue
  useWatchContractEvent({
    address: CONTRACT_ADDRESS ? (CONTRACT_ADDRESS as `0x${string}`) : undefined,
    abi: RedEnvelopeABI,
    eventName: 'EnvelopeClaimed',
    enabled: !!CONTRACT_ADDRESS,
    onLogs(logs: any[]) {
      const log = logs[logs.length - 1];
      if (log && log.args && log.args.claimer === address) {
        const amount = log.args.amount || BigInt(0);
        setMessage({
          type: 'success',
          text: `恭喜！你抢到了 ${formatEther(amount)} ETH！`,
        });
        setTimeout(() => refetchEnvelope(), 2000);
      }
    },
  });

  // @ts-expect-error - Wagmi v3 type definitions issue
  useWatchContractEvent({
    address: CONTRACT_ADDRESS ? (CONTRACT_ADDRESS as `0x${string}`) : undefined,
    abi: RedEnvelopeABI,
    eventName: 'EnvelopeFinished',
    enabled: !!CONTRACT_ADDRESS,
    onLogs() {
      setMessage({
        type: 'info',
        text: '红包已被抢完！',
      });
      setTimeout(() => refetchEnvelope(), 2000);
    },
  });

  // @ts-expect-error - Wagmi v3 type definitions issue
  useWatchContractEvent({
    address: CONTRACT_ADDRESS ? (CONTRACT_ADDRESS as `0x${string}`) : undefined,
    abi: RedEnvelopeABI,
    eventName: 'AlreadyClaimed',
    enabled: !!CONTRACT_ADDRESS,
    onLogs(logs: any[]) {
      const log = logs[logs.length - 1];
      if (log && log.args && log.args.claimer === address) {
        setMessage({
          type: 'error',
          text: '你已经抢过这个红包了！',
        });
      }
    },
  });

  // @ts-expect-error - Wagmi v3 type definitions issue
  useWatchContractEvent({
    address: CONTRACT_ADDRESS ? (CONTRACT_ADDRESS as `0x${string}`) : undefined,
    abi: RedEnvelopeABI,
    eventName: 'EnvelopeEmpty',
    enabled: !!CONTRACT_ADDRESS,
    onLogs() {
      setMessage({
        type: 'error',
        text: '红包已经被抢完了！',
      });
      setTimeout(() => refetchEnvelope(), 2000);
    },
  });

  // 更新红包信息
  useEffect(() => {
    if (envelopeData) {
      setEnvelopeInfo(envelopeData as unknown as EnvelopeInfo);
    }
  }, [envelopeData]);

  // 从 URL 参数获取红包ID
  useEffect(() => {
    const id = searchParams.get('envelopeId');
    if (id) {
      setEnvelopeId(id);
      setTimeout(() => refetchEnvelope(), 1000);
    }
  }, [searchParams, refetchEnvelope]);

  // 处理抢红包
  const handleClaimEnvelope = () => {
    if (!isConnected) {
      setMessage({ type: 'error', text: '请先连接钱包' });
      return;
    }

    if (!envelopeId) {
      setMessage({ type: 'error', text: '请输入红包ID' });
      return;
    }

    if (hasClaimed) {
      setMessage({ type: 'error', text: '你已经抢过这个红包了！' });
      return;
    }

    if (!claimEnvelope) {
      setMessage({ type: 'error', text: '钱包未连接或合约地址未配置' });
      return;
    }

    try {
      (claimEnvelope as any)({
        address: CONTRACT_ADDRESS as `0x${string}`,
        abi: RedEnvelopeABI,
        functionName: 'claimEnvelope',
        args: [BigInt(envelopeId)],
      });
      setMessage({ type: 'info', text: '交易已提交，等待确认...' });
    } catch (error: any) {
      setMessage({ type: 'error', text: error.message || '抢红包失败' });
    }
  };

  // 查询红包信息
  const handleQueryEnvelope = () => {
    if (!envelopeId) {
      setMessage({ type: 'error', text: '请输入红包ID' });
      return;
    }
    refetchEnvelope();
  };

  return (
    <div className="claim-envelope-page">
      <div className="page-header">
        <h1>🎁 抢红包</h1>
        <p className="page-description">输入红包ID，查看并抢红包</p>
      </div>

      <div className="message-container">
        {message && (
          <div className={`message message-${message.type}`}>
            {message.text}
            <button onClick={() => setMessage(null)} className="close-message">×</button>
          </div>
        )}
      </div>

      <div className="claim-envelope-form">
        <div className="form-group">
          <label>红包ID:</label>
          <input
            type="number"
            value={envelopeId}
            onChange={(e) => setEnvelopeId(e.target.value)}
            placeholder="输入红包ID"
          />
          <small>输入要查询的红包ID</small>
        </div>

        <button
          onClick={handleQueryEnvelope}
          className="query-button"
        >
          查询红包信息
        </button>

        {envelopeInfo && (
          <div className="envelope-info">
            <h3>红包信息</h3>
            <div className="info-grid">
              <div className="info-item">
                <span className="info-label">创建者:</span>
                <span className="info-value">{envelopeInfo.creator.slice(0, 6)}...{envelopeInfo.creator.slice(-4)}</span>
              </div>
              <div className="info-item">
                <span className="info-label">总金额:</span>
                <span className="info-value">{formatEther(envelopeInfo.totalAmount)} ETH</span>
              </div>
              <div className="info-item">
                <span className="info-label">剩余金额:</span>
                <span className="info-value">{formatEther(envelopeInfo.remainingAmount)} ETH</span>
              </div>
              <div className="info-item">
                <span className="info-label">总个数:</span>
                <span className="info-value">{envelopeInfo.totalCount.toString()}</span>
              </div>
              <div className="info-item">
                <span className="info-label">剩余个数:</span>
                <span className="info-value">{envelopeInfo.remainingCount.toString()}</span>
              </div>
              <div className="info-item">
                <span className="info-label">状态:</span>
                <span className={`info-value status ${envelopeInfo.isActive ? 'active' : 'finished'}`}>
                  {envelopeInfo.isActive ? '进行中' : '已结束'}
                </span>
              </div>
            </div>
            {hasClaimed && (
              <div className="claimed-warning">
                <span>⚠️ 你已经抢过这个红包了</span>
              </div>
            )}
          </div>
        )}

        <button
          onClick={handleClaimEnvelope}
          disabled={isClaiming || isClaimingConfirming || !isConnected || !envelopeInfo?.isActive || hasClaimed}
          className="claim-button"
        >
          {isClaiming || isClaimingConfirming ? '处理中...' : '抢红包'}
        </button>

        {!isConnected && (
          <p className="warning-text">⚠️ 请先连接钱包</p>
        )}
      </div>
    </div>
  );
};

export default ClaimEnvelope;

